/**
 * Farm Manager Agent - Gemini 2.5 Pro Specialized
 * 
 * This agent orchestrates conversations with the Farm Manager AI assistant.
 * It uses Gemini 2.5 Pro exclusively for all AI interactions, handling streaming
 * responses, function calling, and conversation management.
 */

import { getFarmManagerModel } from "./modelProvider";
import { buildFarmManagerPrompt } from "./promptBuilder";
import { buildHistoryWithContext } from "./historyManager";
import { trackAIChatUsage } from "../aiChatUsageTracking";
import { getFarmManagerSettings } from "../../database/farmManagerSettings";
import { executeChatTool } from "../chatToolsService";
import { validateResponseQuality } from "./responseFormatter";

/**
 * Maps tool IDs to user-friendly progress messages
 * @param {string} toolId - Tool ID (e.g., "suspicious-animals", "disease-overview")
 * @param {string} language - Language code (e.g., "en", "de")
 * @returns {string} User-friendly progress message
 */
function getToolProgressMessage(toolId, language = "en") {
        const isGerman = language.startsWith("de") || language === "ger";
        
        const progressMessages = {
                "suspicious-animals": isGerman ? "Prüfe Ihre Tiere..." : "Checking your animals...",
                "disease-overview": isGerman ? "Lade Krankheitsübersicht..." : "Loading disease overview...",
                "birth-predictions": isGerman ? "Analysiere Geburtsvorhersagen..." : "Analyzing birth predictions...",
                "work-planner-overview": isGerman ? "Prüfe den Arbeitsplaner..." : "Reviewing work planner...",
                "work-planner-workers": isGerman ? "Lade Mitarbeiterinformationen..." : "Loading worker information...",
                "work-planner-create-task": isGerman ? "Erstelle Aufgabe..." : "Creating task...",
                "animal-details": isGerman ? "Lade Tierdetails..." : "Loading animal details...",
                "treatment-history": isGerman ? "Prüfe Behandlungsverlauf..." : "Checking treatment history...",
                "birth-history": isGerman ? "Prüfe Geburtsverlauf..." : "Checking birth history...",
                "vaccination-status": isGerman ? "Prüfe Impfstatus..." : "Checking vaccination status...",
                "event-history": isGerman ? "Prüfe Ereignisverlauf..." : "Checking event history...",
                "bionode-status": isGerman ? "Prüfe BioNode-Status..." : "Checking BioNode status...",
                "bionode-analysis": isGerman ? "Analysiere BioNode-Daten..." : "Analyzing BioNode data...",
                "farm-kpi-overview": isGerman ? "Lade Betriebs-KPIs..." : "Loading farm KPIs...",
                "farm-kpi-trends": isGerman ? "Analysiere KPI-Trends..." : "Analyzing KPI trends...",
                "account-overview": isGerman ? "Lade Kontoinformationen..." : "Loading account information...",
                "billing-status": isGerman ? "Prüfe Abrechnungsstatus..." : "Checking billing status...",
                "notification-preferences": isGerman ? "Lade Benachrichtigungseinstellungen..." : "Loading notification preferences...",
                "search-knowledge-base": isGerman ? "Durchsuche Wissensdatenbank..." : "Searching knowledge base...",
                "production-history": isGerman ? "Lade Produktionsdaten..." : "Loading production records...",
                // New diagnostic and analytical tools
                "health-diagnosis-assist": isGerman ? "Analysiere Gesundheitsdaten..." : "Analyzing health data...",
                "comparative-analysis": isGerman ? "Führe Vergleichsanalyse durch..." : "Running comparative analysis...",
                "generate-report": isGerman ? "Erstelle Bericht..." : "Generating report...",
                "activity-anomaly-analysis": isGerman ? "Analysiere Aktivitätsanomalien..." : "Analyzing activity anomalies...",
                "feeding-analysis": isGerman ? "Analysiere Fütterungsdaten..." : "Analyzing feeding data...",
        };

        return progressMessages[toolId] || (isGerman ? "Führe Aktion aus..." : "Executing action...");
}

/**
 * Gets thinking message based on language
 * @param {string} language - Language code
 * @returns {string} Thinking message
 */
function getThinkingMessage(language = "en") {
        const isGerman = language.startsWith("de") || language === "ger";
        return isGerman ? "Analysiere Ergebnisse..." : "Analyzing results...";
}

function mapErrorToUserMessage(error) {
        const message = error?.message || "";
        const lc = message.toLowerCase();

        if (lc.includes("quota")) {
                return {
                        type: "QUOTA_EXCEEDED",
                        description: "AI service quota exceeded. Please try again later.",
                };
        }

        if (lc.includes("api-not-enabled") || lc.includes("api not enabled")) {
                return {
                        type: "API_NOT_ENABLED",
                        description: "The AI service is not enabled for this project. Please contact support.",
                };
        }

        if (lc.includes("not found") || lc.includes("404") || lc.includes("not supported for generatecontent")) {
                return {
                        type: "MODEL_NOT_FOUND",
                        description: "The AI model is temporarily unavailable. Please try again later.",
                };
        }

        if (lc.includes("permission")) {
                return {
                        type: "PERMISSION_DENIED",
                        description: "Permission denied. Please check your Firebase configuration.",
                };
        }

        if (lc.includes("network") || error?.code === "unavailable") {
                return {
                        type: "NETWORK_ERROR",
                        description: "Network error. Please check your connection and try again.",
                };
        }

        if (lc.includes("invalid-content") || lc.includes("can't follow")) {
                return {
                        type: "INVALID_CONTENT",
                        description: "Invalid conversation history detected. Please try again or start a new chat.",
                };
        }

        return {
                type: "UNKNOWN_ERROR",
                description: "Failed to get AI response. Please try again.",
        };
}

export class FarmManagerAgent {
        constructor() {
                this.model = null;
                this.lastSettingsKey = null;
        }

        /**
         * Ensures the Gemini 2.5 Pro model instance is initialized and up-to-date
         * @param {string} userId - User ID
         * @param {Object} settings - User settings for generation parameters
         * @returns {Promise<Object>} Gemini 2.5 Pro model instance
         */
        async ensureModel(userId, settings) {
                // Check if generation settings changed - if so, reset Gemini 2.5 Pro model to get new config
                const settingsKey = settings ? JSON.stringify(settings.model) : null;
                if (this.lastSettingsKey !== settingsKey) {
                        this.model = null;
                        this.lastSettingsKey = settingsKey;
                }

                if (!this.model) {
                        this.model = await getFarmManagerModel(userId, settings);
                }

                return this.model;
        }

        async streamResponse({
                userId,
                message,
                userContext,
                conversationHistory = [],
                onChunk,
                language = "en",
        }) {
                const startTime = Date.now();
                let aggregatedResponse = "";
                let success = false;
                let errorInfo = null;
                const MAX_FUNCTION_CALL_ITERATIONS = 5; // Prevent infinite loops

                // Helper function to get translation function
                // Note: This is a fallback - actual translations happen in the UI layer
                const getT = () => {
                        return (key, params) => {
                                if (params && typeof params === "object") {
                                        return key.replace(/\{(\w+)\}/g, (match, param) => {
                                                return params[param] !== undefined ? String(params[param]) : match;
                                        });
                                }
                                return key;
                        };
                };

                try {
                        // Load user settings for customization
                        const userSettings = await getFarmManagerSettings(userId);
                        
                        // Get Gemini 2.5 Pro model instance
                        const model = await this.ensureModel(userId, userSettings);
                        
                        // Build conversation context for knowledge retrieval (current message + last 3 messages)
                        const recentMessages = conversationHistory.slice(-3);
                        const conversationContext = [message, ...recentMessages.map(msg => 
                                typeof msg === "object" ? (msg.content || msg.text || "") : String(msg)
                        )].filter(Boolean).join(" ");
                        
                        // Build history with context summarization for longer conversations
                        const { geminiHistory, contextSummary } = buildHistoryWithContext(
                                conversationHistory,
                                userSettings,
                                language
                        );

                        const systemPrompt = await buildFarmManagerPrompt(userContext, {
                                language,
                                conversationContext,
                                settings: userSettings,
                                conversationSummary: contextSummary
                        });
                        const history = geminiHistory;

                        const chat = model.startChat({
                                systemInstruction: { parts: [{ text: systemPrompt }] },
                                history: history.length > 0 ? history : undefined,
                        });

                        let currentMessage = message;
                        let iterationCount = 0;
                        let hasFunctionCalls = false;

                        // Loop to handle function calls
                        while (iterationCount < MAX_FUNCTION_CALL_ITERATIONS) {
                                let streamResult = null;
                                
                                // Wrap sendMessageStream in try-catch to handle SDK errors
                                try {
                                        streamResult = await chat.sendMessageStream(currentMessage);
                                } catch (sendError) {
                                        console.error(
                                                "[FarmManagerAgent][streamResponse] - Error calling sendMessageStream:",
                                                sendError
                                        );
                                        // Try to extract error details for better logging
                                        if (sendError?.message) {
                                                console.error(
                                                        "[FarmManagerAgent][streamResponse] - Error message:",
                                                        sendError.message
                                                );
                                        }
                                        throw sendError;
                                }
                                
                                // Validate streamResult structure
                                if (!streamResult) {
                                        throw new Error("sendMessageStream returned null or undefined");
                                }
                                
                                const { stream } = streamResult || {};
                                
                                // First, check if there are function calls in the response
                                let functionCalls = [];
                                let responseText = "";
                                let streamFailed = false;
                                let chunksAlreadyStreamed = false;
                                
                                // Try to get aggregated response first as a safety net
                                let aggregatedResponsePromise = null;
                                let aggregatedResponseSource = null;
                                if (typeof streamResult?.response?.then === "function") {
                                        aggregatedResponsePromise = streamResult.response;
                                        aggregatedResponseSource = "response";
                                } else if (streamResult?.aggregatedResponse && typeof streamResult.aggregatedResponse.then === "function") {
                                        aggregatedResponsePromise = streamResult.aggregatedResponse;
                                        aggregatedResponseSource = "aggregatedResponse";
                                }
                                
                                // Attempt streaming only if we have a stream and onChunk callback
                                // If streaming fails, we'll fall back to aggregated response
                                if (stream && typeof stream[Symbol.asyncIterator] === "function" && onChunk) {
                                        try {
                                                // Use Promise.race to detect if streaming fails quickly
                                                // eslint-disable-next-line no-loop-func
                                                const streamPromise = (async () => {
                                                        for await (const chunk of stream) {
                                                                try {
                                                                        // Check for function calls in various possible locations
                                                                        const chunkFunctionCalls = 
                                                                                (typeof chunk?.functionCalls === "function" ? chunk.functionCalls() : null) ||
                                                                                (Array.isArray(chunk?.functionCalls) ? chunk.functionCalls : null) ||
                                                                                (typeof chunk?.response?.functionCalls === "function" ? chunk.response.functionCalls() : null) ||
                                                                                (Array.isArray(chunk?.response?.functionCalls) ? chunk.response.functionCalls : null);
                                                                        
                                                                        if (chunkFunctionCalls && Array.isArray(chunkFunctionCalls) && chunkFunctionCalls.length > 0) {
                                                                                functionCalls = chunkFunctionCalls;
                                                                                hasFunctionCalls = true;
                                                                        }
                                                                        
                                                                        const chunkText = typeof chunk?.text === "function"
                                                                                ? chunk.text()
                                                                                : typeof chunk?.response?.text === "function"
                                                                                        ? chunk.response.text()
                                                                                        : null;

                                                                        if (chunkText) {
                                                                                responseText += chunkText;
                                                                                // Stream chunks in real-time
                                                                                onChunk(chunkText);
                                                                                chunksAlreadyStreamed = true;
                                                                        }
                                                                } catch (chunkError) {
                                                                        // Log but continue processing
                                                                        console.warn(
                                                                                "[FarmManagerAgent][streamResponse] - Error processing chunk:",
                                                                                chunkError
                                                                        );
                                                                        continue;
                                                                }
                                                        }
                                                })();
                                                
                                                // Wait for stream to complete
                                                await streamPromise;
                                                // If we got here without errors and have responseText, chunks were streamed
                                                if (responseText && !streamFailed) {
                                                                chunksAlreadyStreamed = true;
                                                }
                                        } catch (streamIterationError) {
                                                // Stream iteration failed - mark for fallback
                                                streamFailed = true;
                                                console.warn(
                                                        "[FarmManagerAgent][streamResponse] - Stream iteration failed, falling back to aggregated response:",
                                                        streamIterationError?.message || streamIterationError
                                                );
                                                // Don't log full error to avoid noise - we have fallback
                                        }
                                }
                                
                                // If streaming failed or wasn't attempted, use aggregated response
                                if (streamFailed || (!responseText && aggregatedResponsePromise)) {
                                        try {
                                                const aggregated = await aggregatedResponsePromise;
                                                
                                                if (aggregated) {
                                                        // Check for function calls in aggregated response
                                                        const aggregatedFunctionCalls = 
                                                                (typeof aggregated?.functionCalls === "function" ? aggregated.functionCalls() : null) ||
                                                                (Array.isArray(aggregated?.functionCalls) ? aggregated.functionCalls : null);
                                                        
                                                        if (aggregatedFunctionCalls && Array.isArray(aggregatedFunctionCalls) && aggregatedFunctionCalls.length > 0) {
                                                                functionCalls = aggregatedFunctionCalls;
                                                                hasFunctionCalls = true;
                                                        }
                                                        
                                                        const aggregatedText = typeof aggregated?.text === "function" ? aggregated.text() : null;
                                                        if (aggregatedText) {
                                                                responseText = aggregatedText;
                                                                // If we're using fallback and have onChunk, stream the full response
                                                                if (onChunk && streamFailed) {
                                                                        onChunk(aggregatedText);
                                                                        chunksAlreadyStreamed = true;
                                                                }
                                                        }
                                                }
                                        } catch (aggregatedError) {
                                                console.error(
                                                        "[FarmManagerAgent][streamResponse] - Error processing aggregated response:",
                                                        aggregatedError
                                                );
                                                // If we don't have responseText yet, this is a problem
                                                if (!responseText) {
                                                        throw new Error("Failed to process both stream and aggregated response");
                                                }
                                        }
                                }

                                // Additional check for function calls if we haven't found any yet
                                // Only try alternative path if we haven't already tried the main aggregated response
                                if (!hasFunctionCalls && !responseText && aggregatedResponseSource !== "aggregatedResponse") {
                                        // Try alternative aggregated response path if not already tried
                                        if (streamResult?.aggregatedResponse && typeof streamResult.aggregatedResponse.then === "function") {
                                                try {
                                                        const altAggregated = await streamResult.aggregatedResponse;
                                                        if (altAggregated) {
                                                                const altFunctionCalls = 
                                                                        (typeof altAggregated?.functionCalls === "function" ? altAggregated.functionCalls() : null) ||
                                                                        (Array.isArray(altAggregated?.functionCalls) ? altAggregated.functionCalls : null);
                                                                if (altFunctionCalls && Array.isArray(altFunctionCalls) && altFunctionCalls.length > 0) {
                                                                        functionCalls = altFunctionCalls;
                                                                        hasFunctionCalls = true;
                                                                }
                                                                
                                                                const altText = typeof altAggregated?.text === "function" ? altAggregated.text() : null;
                                                                if (altText) {
                                                                        responseText = altText;
                                                                        if (onChunk) {
                                                                                onChunk(altText);
                                                                                chunksAlreadyStreamed = true;
                                                                        }
                                                                }
                                                        }
                                                } catch (altError) {
                                                        console.warn(
                                                                "[FarmManagerAgent][streamResponse] - Error processing alternative aggregated response:",
                                                                altError
                                                        );
                                                }
                                        }
                                }
                                
                                // Also check the streamResult directly
                                if (!hasFunctionCalls) {
                                        const directFunctionCalls = 
                                                (typeof streamResult?.functionCalls === "function" ? streamResult.functionCalls() : null) ||
                                                (Array.isArray(streamResult?.functionCalls) ? streamResult.functionCalls : null);
                                        
                                        if (directFunctionCalls && Array.isArray(directFunctionCalls) && directFunctionCalls.length > 0) {
                                                functionCalls = directFunctionCalls;
                                                hasFunctionCalls = true;
                                        }
                                }

                                // If we have function calls, execute them and continue the conversation
                                if (hasFunctionCalls && functionCalls.length > 0) {
                                        const t = getT();
                                        const functionResponses = [];

                                        // Provide user-friendly feedback for each tool being executed
                                        if (onChunk) {
                                                // Show progress messages for each tool
                                                const uniqueToolIds = [...new Set(functionCalls.map(fc => fc.name || "tool"))];
                                                for (const toolId of uniqueToolIds) {
                                                        const progressMessage = getToolProgressMessage(toolId, language);
                                                        onChunk(`\n${progressMessage}\n`);
                                                }
                                        }

                                        for (const functionCall of functionCalls) {
                                                const functionName = functionCall.name;
                                                const args = functionCall.args || {};

                                                try {
                                                        // Execute the tool
                                                        const toolResult = await executeChatTool(
                                                                functionName,
                                                                userId,
                                                                t,
                                                                language,
                                                                { payload: args, source: "function-call" }
                                                        );

                                                        // Format the result for the model
                                                        // Firebase AI expects function response to be a Struct (object), not a string
                                                        const functionResponse = {
                                                                name: functionName,
                                                                response: {
                                                                        result: toolResult?.message || "Tool executed successfully",
                                                                        metadata: toolResult?.metadata || {},
                                                                },
                                                        };

                                                        functionResponses.push(functionResponse);
                                                } catch (toolError) {
                                                        console.error(
                                                                `[FarmManagerAgent][streamResponse] - Error executing function ${functionName}:`,
                                                                toolError
                                                        );
                                                        
                                                        // Return error to model as structured object
                                                        functionResponses.push({
                                                                name: functionName,
                                                                response: {
                                                                        error: toolError?.message || "Failed to execute tool",
                                                                },
                                                        });
                                                }
                                        }

                                        // Show thinking indicator before sending results back to AI
                                        if (onChunk) {
                                                const thinkingMessage = getThinkingMessage(language);
                                                onChunk(`\n${thinkingMessage}\n`);
                                        }

                                        // Send function results back to the model
                                        // Firebase AI expects function responses as parts array directly
                                        currentMessage = functionResponses.map(fr => ({
                                                functionResponse: {
                                                        name: fr.name,
                                                        response: fr.response,
                                                },
                                        }));
                                        
                                        iterationCount++;
                                        hasFunctionCalls = false;
                                        continue; // Continue the loop to get the model's response to function results
                                }

                                // No function calls, return the text response
                                aggregatedResponse = responseText;
                                
                                // Validate we have a response
                                if (!aggregatedResponse && !hasFunctionCalls) {
                                        console.warn(
                                                "[FarmManagerAgent][streamResponse] - No response text received, checking for alternative response formats"
                                        );
                                        // Last resort: try to extract text from streamResult directly
                                        if (streamResult) {
                                                try {
                                                        const directText = typeof streamResult?.text === "function" 
                                                                ? streamResult.text() 
                                                                : streamResult?.text || null;
                                                        if (directText) {
                                                                aggregatedResponse = directText;
                                                        }
                                                } catch (extractError) {
                                                        console.error(
                                                                "[FarmManagerAgent][streamResponse] - Error extracting direct text:",
                                                                extractError
                                                        );
                                                }
                                        }
                                }
                                
                                // Stream the final response if we haven't already streamed it
                                if (onChunk && aggregatedResponse && !responseText.includes("[Executing tools") && !chunksAlreadyStreamed) {
                                        // Only stream if we haven't already streamed chunks
                                        // Check if we've accumulated text that wasn't streamed
                                        if (iterationCount === 0 || hasFunctionCalls === false) {
                                                // Stream the complete response
                                                onChunk(aggregatedResponse);
                                        }
                                }
                                
                                break; // Exit the loop
                        }

                        if (iterationCount >= MAX_FUNCTION_CALL_ITERATIONS) {
                                console.warn(
                                        "[FarmManagerAgent][streamResponse] - Reached max function call iterations"
                                );
                        }

                        // Validate response quality (for logging/analytics purposes)
                        if (aggregatedResponse) {
                                const validationContext = {
                                        isDataQuery: /how many|count|list|show me|what.*animals|status/i.test(message),
                                        isRecommendation: /what should|recommend|suggest|advice|help me/i.test(message),
                                        animalIds: [], // Could extract from response if needed
                                };
                                const validation = validateResponseQuality(aggregatedResponse, validationContext);
                                if (!validation.valid) {
                                        console.warn(
                                                "[FarmManagerAgent][streamResponse] - Response quality issues detected:",
                                                validation.issues,
                                                "Suggestions:",
                                                validation.suggestions
                                        );
                                }
                        }

                        success = true;
                        return aggregatedResponse;
                } catch (error) {
                        console.error("[FarmManagerAgent][streamResponse] - Error streaming message:", error);
                        errorInfo = mapErrorToUserMessage(error);
                        throw new Error(errorInfo.description);
                } finally {
                        const responseTime = Date.now() - startTime;
                        trackAIChatUsage({
                                userId,
                                message,
                                response: aggregatedResponse,
                                responseTime,
                                conversationHistory,
                                success,
                                errorType: errorInfo?.type || null,
                                errorMessage: errorInfo?.description || null,
                                isStreamed: true,
                        }).catch((trackingError) => {
                                console.warn("[FarmManagerAgent][streamResponse] - Usage tracking failed:", trackingError);
                        });
                }
        }
}

let agentInstance = null;

export function getFarmManagerAgent() {
        if (!agentInstance) {
                agentInstance = new FarmManagerAgent();
        }

        return agentInstance;
}
